// packages/api/src/hooks/useMyOrganization.ts
// Source: docs/contracts/ESCR-profile-selfservice.md (adendo PJ)

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import type { ApiSuccess, MyOrganization, UpdateMyOrganizationInput } from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

/**
 * GET /profile/organization — dados da organização do consultor PJ.
 *
 * 404 é estado esperado (party person / PJ migrado sem organizations row),
 * não erro transitório: sem retry e a UI esconde a seção. O gate da seção
 * "Dados da empresa" é este GET retornar 200 — nunca consultantType.
 */
export function useMyOrganization(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.profile.organization(),
    queryFn: async (): Promise<MyOrganization> => {
      const response = await apiClient.get<ApiSuccess<MyOrganization>>('/profile/organization');
      return response.data.data;
    },
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error) && error.response?.status === 404) return false;
      return failureCount < 2;
    },
    enabled: options?.enabled ?? true,
  });
}

/**
 * PATCH /profile/organization — partial update. CNPJ é aceito somente na
 * janela de correção do cadastro (under_review/correction_needed) — fora dela
 * o backend responde 409 ORGANIZATION_CNPJ_LOCKED_FOR_STATUS.
 * Contract: docs/contracts/ESCR-profile-organization-cnpj.md.
 */
export function useUpdateMyOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateMyOrganizationInput): Promise<MyOrganization> => {
      const response = await apiClient.patch<ApiSuccess<MyOrganization>>(
        '/profile/organization',
        input,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.organization() });
      // Corrigir dados da empresa pode limpar a rejeição do bloco no fluxo de correção.
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.myRegistration() });
      queryClient.invalidateQueries({ queryKey: ['consultant-responsible-documents'] });
    },
  });
}
